import { db } from "../../../config/firebaseConfig";
import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import {
  uploadToS3,
  downloadFromS3,
} from "./s3StorageService";

const COLLECTION_NAME = "code_space_backpack";
export const getSnippetsDocId = (studentId, shard = 1) =>
  shard > 1 ? `snippet_${studentId}_${shard}` : `snippet_${studentId}`;
export const getSpritesDocId = (studentId, shard = 1) =>
  shard > 1 ? `sprite_${studentId}_${shard}` : `sprite_${studentId}`;

/*
 * ============================================================================
 * SNIPPETS CRUD OPERATIONS
 * Stored under: code_space_backpack/{childId}_snippets (items array)
 * ============================================================================
 */

/**
 * Save or update a snippet in student's code_space_backpack.
 */
export const saveSnippet = async ({
  snippetId,
  childId,
  parentUid,
  name,
  description = "",
  environment = "scratch", // "scratch" | "python" | "javascript"
  snippetJson = null,
  code = null,
  language = null,
}) => {
  try {
    if (!childId) {
      throw new Error("childId is required to save snippet");
    }

    const targetId = snippetId || (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `snip_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`);
    const now = Date.now();

    // Determine cloud file extension and storage path solely from environment
    const resolveSnippetFileType = (env) => {
      if (env === "scratch" || env === "junior") return "json";
      if (env === "javascript" || env === "js") return "js";
      if (env === "python" || env === "py") return "py";
      if (env === "cpp" || env === "c++") return "cpp";
      return "txt";
    };

    const fileType = resolveSnippetFileType(environment);
    const filePath = `snippets/${childId}/${targetId}.${fileType}`;

    // Upload snippet payload directly to AWS S3
    if (environment === "scratch" || environment === "junior") {
      const content =
        typeof snippetJson === "string"
          ? snippetJson
          : JSON.stringify(snippetJson || {}, null, 2);
      await uploadToS3(filePath, content, "application/json");
    } else {
      const mime =
        fileType === "py"
          ? "text/x-python"
          : fileType === "js"
          ? "application/javascript"
          : "text/plain";
      await uploadToS3(filePath, code || "", mime);
    }

    // Clean snippet metadata item (NO raw code, NO snippetJson, and NO redundant type!)
    const snippetItem = {
      id: targetId,
      name: name?.trim() || "Unnamed Snippet",
      description: description?.trim() || "",
      environment,
      fileType,
      filePath,
      createdAt: now,
      updatedAt: now,
    };

    const docRef = doc(db, COLLECTION_NAME, getSnippetsDocId(childId));

    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(docRef);
      let items = [];
      let createdAt = now;

      if (snap.exists()) {
        items = snap.data().items || [];
      }

      const existingIndex = items.findIndex((item) => item.id === targetId);
      if (existingIndex >= 0) {
        createdAt = items[existingIndex].createdAt || now;
        snippetItem.createdAt = createdAt;
        items[existingIndex] = { ...items[existingIndex], ...snippetItem };
      } else {
        snippetItem.createdAt = now;
        items.unshift(snippetItem);
        if (items.length > 50) items = items.slice(0, 50);
      }

      transaction.set(
        docRef,
        {
          type: "snippets",
          childId,
          parentUid: parentUid || null,
          updatedAt: serverTimestamp(),
          items,
        },
        { merge: true }
      );
    });

    return snippetItem;
  } catch (error) {
    console.error("Error saving snippet to code_space_backpack:", error);
    throw error;
  }
};

/**
 * Fetch all saved snippets for a student from code_space_backpack/{childId}_snippets.
 * Executes a single point lookup (1 read).
 */
export const getSavedSnippets = async (childId, environment = null) => {
  try {
    if (!childId) return [];

    const primaryDocRef = doc(db, COLLECTION_NAME, getSnippetsDocId(childId, 1));
    const snap = await getDoc(primaryDocRef);

    let items = [];
    if (snap.exists()) {
      items = snap.data().items || [];
    }

    // Check if secondary shard exists (snippet_{childId}_2)
    try {
      const secDocRef = doc(db, COLLECTION_NAME, getSnippetsDocId(childId, 2));
      const secSnap = await getDoc(secDocRef);
      if (secSnap.exists()) {
        const secItems = secSnap.data().items || [];
        items = items.concat(secItems);
      }
    } catch (e) {
      // Shard 2 optional
    }

    if (environment) {
      items = items.filter((s) => s.environment === environment);
    }

    // Sort descending by updatedAt
    items.sort((a, b) => {
      const timeA = typeof a.updatedAt === "number" ? a.updatedAt : (a.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0);
      const timeB = typeof b.updatedAt === "number" ? b.updatedAt : (b.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0);
      return timeB - timeA;
    });

    return items;
  } catch (error) {
    console.error("Error fetching snippets from code_space_backpack:", error);
    throw error;
  }
};

/**
 * Delete a snippet from student's code_space_backpack (snippet_{childId}).
 */
export const deleteSnippet = async (childId, snippetId) => {
  try {
    if (!childId || !snippetId) throw new Error("childId and snippetId required");

    const docRef = doc(db, COLLECTION_NAME, getSnippetsDocId(childId, 1));
    let deleted = false;

    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(docRef);
      if (snap.exists()) {
        const currentItems = snap.data().items || [];
        const updatedItems = currentItems.filter((item) => item.id !== snippetId);
        if (updatedItems.length !== currentItems.length) {
          transaction.update(docRef, {
            items: updatedItems,
            updatedAt: serverTimestamp(),
          });
          deleted = true;
        }
      }
    });

    if (!deleted) {
      // Check secondary shard (snippet_{childId}_2)
      const secDocRef = doc(db, COLLECTION_NAME, getSnippetsDocId(childId, 2));
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(secDocRef);
        if (snap.exists()) {
          const currentItems = snap.data().items || [];
          const updatedItems = currentItems.filter((item) => item.id !== snippetId);
          if (updatedItems.length !== currentItems.length) {
            transaction.update(secDocRef, {
              items: updatedItems,
              updatedAt: serverTimestamp(),
            });
          }
        }
      });
    }

    return true;
  } catch (error) {
    console.error("Error deleting snippet from code_space_backpack:", error);
    throw error;
  }
};

/*
 * ============================================================================
 * SPRITES CRUD OPERATIONS
 * Stored under: code_space_backpack/{childId}_sprites (items array)
 * ============================================================================
 */

/**
 * Save or update a sprite in student's code_space_backpack.
 */
export const saveSprite = async ({
  spriteId,
  childId,
  parentUid,
  name,
  description = "",
  spriteCode,
  thumbnail,
  thumbnailPath = null,
}) => {
  try {
    if (!childId) {
      throw new Error("childId is required to save sprite");
    }

    const targetId = spriteId || (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `sprite_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`);
    const now = Date.now();

    const filePath = `sprites/${childId}/${targetId}.sprite3`;

    // Upload sprite3 package directly to AWS S3
    if (spriteCode) {
      await uploadToS3(filePath, spriteCode, "application/x.scratch.sprite3");
    }

    // Upload sprite costume thumbnail directly to AWS S3 if provided
    let resolvedThumbnailPath = thumbnailPath;
    if (thumbnail) {
      resolvedThumbnailPath = `sprites/${childId}/${targetId}_thumb.webp`;
      try {
        await uploadToS3(resolvedThumbnailPath, thumbnail, "image/webp");
      } catch (thumbErr) {
        console.warn("Failed to upload sprite thumbnail to S3:", thumbErr);
      }
    }

    // Clean sprite metadata item (NO heavy .sprite3 payload or Base64 thumbnail bloat in Firestore!)
    const spriteItem = {
      id: targetId,
      name: name?.trim() || "Unnamed Sprite",
      description: description?.trim() || "",
      fileType: "sprite3",
      filePath,
      thumbnailPath: resolvedThumbnailPath,
      createdAt: now,
      updatedAt: now,
    };

    const docRef = doc(db, COLLECTION_NAME, getSpritesDocId(childId));

    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(docRef);
      let items = [];
      let createdAt = now;

      if (snap.exists()) {
        items = snap.data().items || [];
      }

      const existingIndex = items.findIndex((item) => item.id === targetId);
      if (existingIndex >= 0) {
        createdAt = items[existingIndex].createdAt || now;
        spriteItem.createdAt = createdAt;
        items[existingIndex] = { ...items[existingIndex], ...spriteItem };
      } else {
        spriteItem.createdAt = now;
        items.unshift(spriteItem);
        if (items.length > 50) items = items.slice(0, 50);
      }

      transaction.set(
        docRef,
        {
          type: "sprites",
          childId,
          parentUid: parentUid || null,
          updatedAt: serverTimestamp(),
          items,
        },
        { merge: true }
      );
    });

    return spriteItem;
  } catch (error) {
    console.error("Error saving sprite to code_space_backpack:", error);
    throw error;
  }
};

/**
 * Fetch all saved sprites for a student from code_space_backpack/{childId}_sprites.
 * Executes a single point lookup (1 read).
 */
export const getSavedSprites = async (childId) => {
  try {
    if (!childId) return [];

    const primaryDocRef = doc(db, COLLECTION_NAME, getSpritesDocId(childId, 1));
    const snap = await getDoc(primaryDocRef);

    let items = [];
    if (snap.exists()) {
      items = snap.data().items || [];
    }

    // Check if secondary shard exists (sprite_{childId}_2)
    try {
      const secDocRef = doc(db, COLLECTION_NAME, getSpritesDocId(childId, 2));
      const secSnap = await getDoc(secDocRef);
      if (secSnap.exists()) {
        const secItems = secSnap.data().items || [];
        items = items.concat(secItems);
      }
    } catch (e) {
      // Shard 2 optional
    }

    // Sort descending by updatedAt
    items.sort((a, b) => {
      const timeA = typeof a.updatedAt === "number" ? a.updatedAt : (a.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0);
      const timeB = typeof b.updatedAt === "number" ? b.updatedAt : (b.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0);
      return timeB - timeA;
    });

    return items;
  } catch (error) {
    console.error("Error fetching sprites from code_space_backpack:", error);
    throw error;
  }
};

/**
 * Delete a sprite from student's code_space_backpack (sprite_{childId}).
 */
export const deleteSprite = async (childId, spriteId) => {
  try {
    if (!childId || !spriteId) throw new Error("childId and spriteId required");

    const docRef = doc(db, COLLECTION_NAME, getSpritesDocId(childId, 1));
    let deleted = false;

    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(docRef);
      if (snap.exists()) {
        const currentItems = snap.data().items || [];
        const updatedItems = currentItems.filter((item) => item.id !== spriteId);
        if (updatedItems.length !== currentItems.length) {
          transaction.update(docRef, {
            items: updatedItems,
            updatedAt: serverTimestamp(),
          });
          deleted = true;
        }
      }
    });

    if (!deleted) {
      // Check secondary shard (sprite_{childId}_2)
      const secDocRef = doc(db, COLLECTION_NAME, getSpritesDocId(childId, 2));
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(secDocRef);
        if (snap.exists()) {
          const currentItems = snap.data().items || [];
          const updatedItems = currentItems.filter((item) => item.id !== spriteId);
          if (updatedItems.length !== currentItems.length) {
            transaction.update(secDocRef, {
              items: updatedItems,
              updatedAt: serverTimestamp(),
            });
          }
        }
      });
    }

    return true;
  } catch (error) {
    console.error("Error deleting sprite from code_space_backpack:", error);
    throw error;
  }
};

/**
 * Load snippet payload (JSON blocks or code string) from S3 or metadata fallback.
 */
export const loadSnippetData = async (snippetItem) => {
  if (!snippetItem) return null;

  if (snippetItem.snippetJson || snippetItem.code) {
    return snippetItem;
  }

  if (!snippetItem.filePath) {
    throw new Error("Snippet has no storage filePath specified.");
  }

  if (
    snippetItem.fileType === "json" ||
    snippetItem.environment === "scratch" ||
    snippetItem.environment === "junior"
  ) {
    const json = await downloadFromS3(snippetItem.filePath, "json");
    return {
      ...snippetItem,
      snippetJson: json,
    };
  }

  const text = await downloadFromS3(snippetItem.filePath, "text");
  return {
    ...snippetItem,
    code: text,
  };
};

/**
 * Load sprite .sprite3 payload as ArrayBuffer for Scratch VM addSprite.
 */
export const loadSpriteData = async (spriteItem) => {
  if (!spriteItem) return null;

  if (spriteItem.spriteCode) {
    return spriteItem.spriteCode;
  }

  if (!spriteItem.filePath) {
    throw new Error("Sprite has no storage filePath specified.");
  }

  const buffer = await downloadFromS3(spriteItem.filePath, "arrayBuffer");
  if (!buffer || buffer.byteLength === 0) {
    throw new Error("Downloaded empty sprite data from cloud storage.");
  }

  const u8 = new Uint8Array(buffer);

  // Auto-heal backward compatibility:
  // If the file was saved as ASCII base64 (e.g. starts with 'UEsDB'), decode it to real binary bytes!
  // In ASCII, 'U'=85, 'E'=69, 's'=115, 'D'=68, 'B'=66
  if (
    u8.length >= 5 &&
    u8[0] === 85 &&
    u8[1] === 69 &&
    u8[2] === 115 &&
    u8[3] === 68 &&
    u8[4] === 66
  ) {
    const decoder = new TextDecoder();
    const base64Str = decoder.decode(u8);
    const bstr = atob(base64Str.trim());
    let n = bstr.length;
    const realU8 = new Uint8Array(n);
    while (n--) {
      realU8[n] = bstr.charCodeAt(n);
    }
    return realU8.buffer;
  }

  return buffer;
};

